/**
 * QR code generator component.
 * Generates and displays a QR code from a URL string using the QRCode library.
 * Renders the QR code on a canvas element with optional scaling.
 */

import { useEffect, useRef } from 'react';
import QRCanvas from '../../atoms/images/qrCanvas';

// QRCode is loaded globally via script tag
declare const QRCode: {
  toCanvas: (canvas: HTMLCanvasElement, text: string) => void;
};

/**
 * Generates a QR code from a URL and renders it on a canvas.
 * Uses the QRCode library to generate the QR code image.
 *
 * @param {object} props - Component props
 * @param {number} props.scale - Optional scale factor for the QR code
 * @param {string} props.url - URL to encode in the QR code
 * @returns {JSX.Element | null} QR code canvas element or null if no URL provided
 */
function QrGenerator({ scale, url }: { scale?: number; url?: string }) {
  if (!url) return null;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    if (canvasRef.current) {
      try {
        QRCode.toCanvas(canvasRef.current, url);
      } catch (error) {
        console.error(error);
      }
    }
  }, [url]);
  return <QRCanvas ref={canvasRef} scale={scale} id={'qr'}></QRCanvas>;
}

export default QrGenerator;
